
import { useState } from 'react';
import { User } from '@supabase/supabase-js';
import { ProfileData } from '@/pages/Profile';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription,
  DialogFooter
} from '@/components/ui/dialog';
import { UserRound, Edit, Image } from 'lucide-react';

interface ProfileHeaderProps {
  user: User;
  profileData: ProfileData;
  updateProfile: (data: Partial<ProfileData>) => Promise<void>;
}

const ProfileHeader = ({ user, profileData, updateProfile }: ProfileHeaderProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [username, setUsername] = useState(profileData.username || '');
  const [avatarUrl, setAvatarUrl] = useState(profileData.avatar_url || '');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await updateProfile({
      username: username || null,
      avatar_url: avatarUrl || null
    });
    setIsEditing(false);
  };

  const getInitials = () => {
    if (profileData.username) {
      return profileData.username.charAt(0).toUpperCase();
    }
    if (user.email) {
      return user.email.charAt(0).toUpperCase();
    }
    return 'U';
  };

  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex flex-col md:flex-row items-center gap-6">
          <div className="relative">
            <Avatar className="h-24 w-24 border-2 border-gray-200">
              {profileData.avatar_url ? (
                <AvatarImage src={profileData.avatar_url} alt={profileData.username || 'Utilisateur'} />
              ) : null}
              <AvatarFallback className="bg-primary text-primary-foreground text-2xl">
                {getInitials()}
              </AvatarFallback>
            </Avatar>
          </div>
          
          <div className="flex-1 text-center md:text-left">
            <h2 className="text-2xl font-bold">
              {profileData.username || user.email}
            </h2>
            <p className="text-muted-foreground">
              {user.email}
            </p>
            <p className="text-sm text-muted-foreground mt-1">
              Membre depuis {new Date(user.created_at || Date.now()).toLocaleDateString('fr-FR')}
            </p>
            
            <div className="mt-4">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setIsEditing(true)}
                className="gap-2"
              >
                <Edit className="h-4 w-4" />
                Modifier le profil
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
      
      <Dialog open={isEditing} onOpenChange={setIsEditing}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modifier votre profil</DialogTitle>
            <DialogDescription>
              Mettez à jour votre nom d'utilisateur et votre photo de profil.
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4 py-4">
            <div className="space-y-2">
              <label htmlFor="username" className="text-sm font-medium">
                Nom d'utilisateur
              </label>
              <div className="flex items-center space-x-2">
                <UserRound className="h-4 w-4 text-muted-foreground" />
                <Input
                  id="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Entrez votre nom d'utilisateur"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <label htmlFor="avatar" className="text-sm font-medium">
                URL de la photo de profil
              </label>
              <div className="flex items-center space-x-2">
                <Image className="h-4 w-4 text-muted-foreground" />
                <Input
                  id="avatar"
                  value={avatarUrl}
                  onChange={(e) => setAvatarUrl(e.target.value)}
                  placeholder="Entrez l'URL de votre photo de profil"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Entrez l'URL d'une image en ligne pour votre photo de profil
              </p>
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsEditing(false)}>
                Annuler
              </Button>
              <Button type="submit">Enregistrer</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default ProfileHeader;
